import { groupBy, min } from 'lib/array/arrayUtils'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { diffInDays } from 'lib/datetime/dateUtils'
import { applyChannelMarkupAndRoundUpToTen, applyChannelMarkupToCombinedRoomsAndRoundUpToTen } from './channelMarkup'
import { resolveChannelMarkupDetails, resolveMemberChannelMarkupDetails } from './channelMarkupV2'
import type { ChannelMarkupSource, ChannelMarkupV2OfferPricing } from './channelMarkupV2'

/**
 * One priced option of an offer, as the lead-package rule reads it. Prices are the base
 * (before channel markup), as public-offer returns them.
 */
export interface ChannelMarkupLeadOption {
  duration: number
  price?: number | null
  luxPlusPrice?: number | null
  surcharge?: number | null
  isSoloTraveller: boolean
  /** Whether the option is one of the offer's `defaultOptions` (public-offer's lowest options). */
  isDefault: boolean
}

/**
 * Pick the lead option of one duration with the same rule as `getLowestPricePackage`, so the lead
 * is the package the offer tile's "from" price shows for that duration:
 *
 * 1. the offer's default options, while the duration has one;
 * 2. solo-traveller rates set aside, while another option is left;
 * 3. the lowest `(luxPlusPrice || price) + surcharge`.
 *
 * Every LE option carries its LuxPlus+ price whoever is looking, so the lead does not depend on
 * the viewer. Options without a price are skipped.
 */
function getLeadOption(options: ReadonlyArray<ChannelMarkupLeadOption>): ChannelMarkupLeadOption | undefined {
  const priced = options.filter(option => (option.price ?? 0) > 0)
  const defaults = priced.filter(option => option.isDefault)
  const candidates = defaults.length > 0 ? defaults : priced
  const nonSolo = candidates.filter(option => !option.isSoloTraveller)
  return min(
    nonSolo.length > 0 ? nonSolo : candidates,
    option => (option.luxPlusPrice || option.price || 0) + (option.surcharge ?? 0) || Number.MAX_SAFE_INTEGER,
  )
}

/**
 * The prices a v2 price-band condition reads for an LE offer (Flash, LPC), per duration.
 *
 * Product rule: a price band is checked on the offer's lead package only — the package the offer
 * tile's "from" price shows for that duration — and, if the lead qualifies, the markup or markdown
 * applies to every package of the offer for that duration. So every surface (tiles, offer page,
 * calendar, checkout) decides on this one number instead of the price it is showing, and the
 * decision is the same wherever the offer is priced.
 *
 * Built from public-offer's static options, which do not change when a package sells out, so a
 * sold-out lead does not flip the decision mid-booking. A duration with no priced option has no
 * entry; reading it gives no price, which fails a price band closed.
 */
export function getChannelMarkupLeadPricingByDuration(
  options: Array<ChannelMarkupLeadOption>,
): App.ChannelMarkupLeadPricingByDuration {
  const leadByDuration: App.ChannelMarkupLeadPricingByDuration = {}
  for (const [duration, durationOptions] of groupBy(options, option => option.duration)) {
    const lead = getLeadOption(durationOptions)
    if (lead?.price) {
      leadByDuration[duration] = { total: lead.price, luxPlusPrice: lead.luxPlusPrice || undefined }
    }
  }
  return leadByDuration
}

/**
 * The lead pricing for one duration of an offer, or `undefined` when the offer has none for it
 * (a price band then fails closed).
 */
export function getChannelMarkupLeadPricing(
  offer: { channelMarkupLeadPricing?: App.ChannelMarkupLeadPricingByDuration } | undefined,
  duration: number | undefined,
): App.ChannelMarkupLeadPricing | undefined {
  if (!offer?.channelMarkupLeadPricing || duration === undefined) {
    return undefined
  }
  return offer.channelMarkupLeadPricing[duration]
}

/** Whether an offer type's v2 price bands are checked on its lead package (Flash, LPC). */
export function isLeadPackageMarkupOfferType(
  offerType: string | undefined,
): offerType is typeof OFFER_TYPE_HOTEL | typeof OFFER_TYPE_ALWAYS_ON {
  return offerType === OFFER_TYPE_HOTEL || offerType === OFFER_TYPE_ALWAYS_ON
}

/**
 * A bedbank (LPP) offer's "from" sell price before channel markup: the lowest price public-offer
 * quotes for one room, for `los` nights.
 */
export interface BedbankChannelMarkupLeadSell {
  price: number
  los: number
}

/**
 * The prices a v2 price band reads for a bedbank (LPP) offer staying `nights`: the offer's "from"
 * sell price before channel markup, scaled from the `los` nights it is quoted for to the stay.
 *
 * Same product rule as Flash/LPC: the band is checked on the offer's lead only and, if the lead
 * qualifies, the markup applies to every rate of the offer for that stay. The "from" sell is
 * public-offer's static lowest price for one room, so it does not move with a dated search or a
 * sold-out room. It carries no LuxPlus+ price, so a `luxPlusPrice` band fails closed for bedbank.
 * Without a "from" sell a price band fails closed. Without `nights` the sell's own `los` is read.
 */
export function getBedbankChannelMarkupLeadPricing(
  leadSell: BedbankChannelMarkupLeadSell | undefined,
  nights: number | undefined,
): App.ChannelMarkupLeadPricing | undefined {
  if (!leadSell?.price || !leadSell.los) {
    return undefined
  }
  return { total: Math.round((leadSell.price / leadSell.los) * (nights || leadSell.los)) }
}

/**
 * The un-marked "from" sell of a bedbank offer, either mapped or raw from public-offer, so every
 * rate fetch reads the lead the same way. A mapped offer's `price` carries the markup and its
 * `basePrice` holds the un-marked price. A raw public-offer sell is un-marked and has no
 * `basePrice`, so `price` is read; the same fallback covers offers held in redux from before
 * `basePrice` existed, where no markup applied and the two were equal.
 */
export function getBedbankOfferChannelMarkupLeadSell(
  offer: { sell?: { price: number, los: number, basePrice?: number | undefined } | undefined },
): BedbankChannelMarkupLeadSell | undefined {
  return offer.sell ? { price: offer.sell.basePrice ?? offer.sell.price, los: offer.sell.los } : undefined
}

/** Whether an offer type's v2 price bands are checked on its lead price (Flash, LPC, bedbank). */
export function isLeadPriceMarkupOfferType(
  offerType: string | undefined,
): offerType is typeof OFFER_TYPE_HOTEL | typeof OFFER_TYPE_ALWAYS_ON | typeof OFFER_TYPE_BED_BANK {
  return isLeadPackageMarkupOfferType(offerType) || offerType === OFFER_TYPE_BED_BANK
}

/** Any loaded offer a lead price can be read from. */
export type ChannelMarkupLeadOffer = App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary

/**
 * Just the part of an offer its lead price is read from — the lead package by duration for
 * Flash/LPC, the "from" sell for bedbank — so it can be kept without holding the whole offer.
 */
export type ChannelMarkupLead =
  | { type: 'leadPackage', leadPricingByDuration: App.ChannelMarkupLeadPricingByDuration }
  | { type: 'bedbankSell', leadSell: BedbankChannelMarkupLeadSell }

/** The lead of any loaded offer, or `undefined` when it has none (a price band then fails closed). */
export function getOfferChannelMarkupLead(
  offer: ChannelMarkupLeadOffer | undefined,
): ChannelMarkupLead | undefined {
  if (!offer) {
    return undefined
  }
  if (offer.type === OFFER_TYPE_BED_BANK) {
    const leadSell = 'sell' in offer ? getBedbankOfferChannelMarkupLeadSell(offer) : undefined
    return leadSell ? { type: 'bedbankSell', leadSell } : undefined
  }
  return 'channelMarkupLeadPricing' in offer && offer.channelMarkupLeadPricing
    ? { type: 'leadPackage', leadPricingByDuration: offer.channelMarkupLeadPricing }
    : undefined
}

/** The lead pricing a v2 price band reads for `duration` nights of an offer's lead. */
export function getChannelMarkupLeadPricingForDuration(
  lead: ChannelMarkupLead | undefined,
  duration: number | undefined,
): App.ChannelMarkupLeadPricing | undefined {
  switch (lead?.type) {
    case 'bedbankSell':
      return getBedbankChannelMarkupLeadPricing(lead.leadSell, duration)
    case 'leadPackage':
      return getChannelMarkupLeadPricing({ channelMarkupLeadPricing: lead.leadPricingByDuration }, duration)
    default:
      return undefined
  }
}

/**
 * The lead pricing a v2 price band reads for `duration` nights of any loaded offer: the lead
 * package for Flash/LPC, the "from" sell for bedbank. Any other offer has none.
 */
export function getOfferChannelMarkupLeadPricing(
  offer: ChannelMarkupLeadOffer | undefined,
  duration: number | undefined,
): App.ChannelMarkupLeadPricing | undefined {
  return getChannelMarkupLeadPricingForDuration(getOfferChannelMarkupLead(offer), duration)
}

/**
 * The prices a v2 price band reads for one priced checkout item: the lead for a lead-priced offer
 * (the lead package for Flash/LPC, the "from" sell for bedbank), the item's own base prices for any
 * other type.
 */
export function getChannelMarkupBandPricing(
  offerType: string | undefined,
  { leadPricing, ownPricing }: {
    leadPricing: App.ChannelMarkupLeadPricing | undefined
    ownPricing: ChannelMarkupV2OfferPricing | undefined
  },
): ChannelMarkupV2OfferPricing | undefined {
  return isLeadPriceMarkupOfferType(offerType) ? leadPricing : ownPricing
}

function markSuggestedTravelDates(
  metaData: App.OfferListMetaData,
  offerType: App.OfferType,
  offer: ChannelMarkupLeadOffer | undefined,
  markupSource: ChannelMarkupSource | undefined,
): App.OfferSuggestedDates | undefined {
  const suggestedTravelDates = metaData.suggestedTravelDates
  if (!suggestedTravelDates?.checkIn || !suggestedTravelDates.checkOut) {
    return suggestedTravelDates
  }
  const nights = diffInDays(new Date(suggestedTravelDates.checkIn), new Date(suggestedTravelDates.checkOut))
  const leadPricing = getOfferChannelMarkupLeadPricing(offer, nights)
  const markupDetails = resolveChannelMarkupDetails(metaData.offerId, offerType, markupSource, leadPricing)
  const memberMarkupDetails = resolveMemberChannelMarkupDetails(metaData.offerId, offerType, markupSource, leadPricing)
  return {
    ...suggestedTravelDates,
    price: suggestedTravelDates.price ? applyChannelMarkupAndRoundUpToTen(suggestedTravelDates.price, markupDetails) : suggestedTravelDates.price,
    memberPrice: suggestedTravelDates.memberPrice ? applyChannelMarkupAndRoundUpToTen(suggestedTravelDates.memberPrice, memberMarkupDetails) : suggestedTravelDates.memberPrice,
  }
}

/**
 * Mark a Flash/LPC/bedbank search result's prices, which the search mapper stores before channel
 * markup (see `App.OfferListMetadataPricing['channelMarkupRoomCount']`). A v2 price band reads the
 * offer's lead price for the result's duration, so the tile's search price carries the same
 * markup as the offer page and checkout. Without the offer a price band fails closed; any other
 * condition still resolves. A result that is not deferred is returned as is.
 */
export function applyLeadChannelMarkupToSearchMetaData(
  metaData: App.OfferListMetaData,
  offer: ChannelMarkupLeadOffer | undefined,
  markupSource: ChannelMarkupSource | undefined,
): App.OfferListMetaData {
  const { pricing, type: offerType } = metaData
  const roomCount = pricing.channelMarkupRoomCount
  if (roomCount === undefined || !isLeadPriceMarkupOfferType(offerType)) {
    return metaData
  }
  const leadPricing = getOfferChannelMarkupLeadPricing(offer, pricing.duration)
  const markupDetails = resolveChannelMarkupDetails(metaData.offerId, offerType, markupSource, leadPricing)
  const memberMarkupDetails = resolveMemberChannelMarkupDetails(metaData.offerId, offerType, markupSource, leadPricing)
  return {
    ...metaData,
    suggestedTravelDates: markSuggestedTravelDates(metaData, offerType, offer, markupSource),
    pricing: {
      ...pricing,
      lowestPrice: pricing.lowestPrice ? applyChannelMarkupToCombinedRoomsAndRoundUpToTen(pricing.lowestPrice, markupDetails, roomCount) : pricing.lowestPrice,
      lowestMemberPrice: pricing.lowestMemberPrice ? applyChannelMarkupToCombinedRoomsAndRoundUpToTen(pricing.lowestMemberPrice, memberMarkupDetails, roomCount) : pricing.lowestMemberPrice,
      channelMarkupRoomCount: undefined,
    },
  }
}
